/**
 * Two web components for the PDP sizing sliders:
 *   <vpv-okendo-fit-slider>    hydrated from Okendo's review_aggregate API
 *   <vpv-garment-slider-block> server-rendered from product metafields
 *
 * Colour swatches swap the product without a page load, so both re-render on
 * `vpv:product:change` — dispatched by custom.js and product-grouping.liquid
 * with detail { doc, url, productId }, where `doc` is the already-parsed
 * markup of the new product. A block with no data for the new product hides
 * itself rather than being removed, so it can come back for another colour.
 */
(function () {
  'use strict';

  const PRODUCT_CHANGE_EVENT = 'vpv:product:change';

  // Okendo centered-range attributes come back on a raw -1 to 1 scale.
  const SCALE_MIN = -1;
  const SCALE_MAX = 1;

  let pendingUrl = null;
  let pendingDoc = null;

  function attrSelector(tag, name, value) {
    return tag + '[' + name + '="' + String(value).replace(/["\\]/g, '\\$&') + '"]';
  }

  function fetchDoc(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error('vpv product html ' + res.status);
        return res.text();
      })
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
  }

  // One document per swatch click, however many blocks are listening.
  function resolveDoc(detail) {
    if (detail.doc) return Promise.resolve(detail.doc);
    if (!detail.url) return Promise.resolve(null);

    if (pendingUrl !== detail.url) {
      pendingUrl = detail.url;
      pendingDoc = fetchDoc(detail.url).catch((error) => {
        pendingUrl = null;
        pendingDoc = null;
        throw error;
      });
    }

    return pendingDoc;
  }

  const aggregates = new Map();

  function aggregateMatches(data, subscriberId, productId) {
    const aggregate = data && data.reviewAggregate;
    if (!aggregate) return false;

    const product = 'shopify-' + productId;

    return [
      [aggregate.productId, product],
      [aggregate.subscriberId, subscriberId],
      [aggregate.subscriberId_productId, subscriberId + ':' + product],
    ].every(([actual, expected]) => !actual || actual === expected);
  }

  function readAggregateCache(cacheKey, subscriberId, productId) {
    let stored = null;
    try { stored = sessionStorage.getItem(cacheKey); } catch (e) {}
    if (!stored) return null;

    let data = null;
    try { data = JSON.parse(stored); } catch (e) {}
    if (data && aggregateMatches(data, subscriberId, productId)) return Promise.resolve(data);

    // A poisoned entry heals itself: drop it and fall through to the network.
    try { sessionStorage.removeItem(cacheKey); } catch (e) {}
    return null;
  }

  function fetchAggregate(subscriberId, productId) {
    const cacheKey = 'vpv-oke-agg-' + subscriberId + '-' + productId;
    if (aggregates.has(cacheKey)) return aggregates.get(cacheKey);

    let request = readAggregateCache(cacheKey, subscriberId, productId);

    if (!request) {
      request = fetch(
        'https://api.okendo.io/v1/stores/' + subscriberId +
        '/products/shopify-' + productId + '/review_aggregate'
      )
        .then((res) => {
          if (!res.ok) throw new Error('okendo aggregate ' + res.status);
          return res.json();
        })
        .then((data) => {
          if (!aggregateMatches(data, subscriberId, productId)) {
            throw new Error('okendo aggregate is not for shopify-' + productId);
          }
          try { sessionStorage.setItem(cacheKey, JSON.stringify(data)); } catch (e) {}
          return data;
        })
        .catch((error) => {
          aggregates.delete(cacheKey);
          throw error;
        });
    }

    aggregates.set(cacheKey, request);
    return request;
  }

  class VpvProductBlock extends HTMLElement {
    connectedCallback() {
      if (!this._onProductChange) {
        this._onProductChange = this.handleProductChange.bind(this);
      }
      document.addEventListener(PRODUCT_CHANGE_EVENT, this._onProductChange);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(PRODUCT_CHANGE_EVENT, this._onProductChange);
    }

    handleProductChange(event) {
      const detail = event.detail || {};
      // Drops the response for a colour the shopper has already clicked past.
      const token = (this._token = {});

      resolveDoc(detail)
        .then((doc) => {
          if (token !== this._token || !this.isConnected) return;
          this.adoptProduct(this.counterpart(doc), detail);
        })
        .catch((error) => {
          if (token !== this._token || !this.isConnected) return;
          this.reset();
          console.warn('[vpv-sliders] could not sync ' + this.localName, error);
        });
    }

    counterpart(doc) {
      if (!doc) return null;

      const byId = this.dataset.blockId &&
        doc.querySelector(attrSelector(this.localName, 'data-block-id', this.dataset.blockId));
      if (byId) return byId;

      // Block ids differ between templates (mens/womens); the instance class
      // is the same block wherever it is used.
      return (this.dataset.blockKey &&
        doc.querySelector(attrSelector(this.localName, 'data-block-key', this.dataset.blockKey))) || null;
    }

    render() {}
    adoptProduct() {}

    reset() {
      this.setAttribute('hidden', '');
    }
  }

  class VpvGarmentSliderBlock extends VpvProductBlock {
    adoptProduct(next) {
      if (!next) return this.reset();

      this.innerHTML = next.innerHTML;
      this.toggleAttribute('hidden', next.hasAttribute('hidden'));
    }

    reset() {
      this.innerHTML = '';
      this.setAttribute('hidden', '');
    }
  }

  class VpvOkendoFitSlider extends VpvProductBlock {
    render() {
      this.hydrate();
    }

    adoptProduct(next, detail) {
      // What changes with the product is the identity — and with it the
      // aggregate to fetch and the default value the markup carries.
      const productId = String((next && next.dataset.productId) || detail.productId || '');
      if (!productId) return this.reset();

      const subscriberId = String((next && next.dataset.subscriberId) || this.dataset.subscriberId || '');

      // The pair, not the product alone — the aggregate is cached under both.
      if (productId === this.dataset.productId &&
          subscriberId === String(this.dataset.subscriberId || '')) {
        return;
      }

      this.dataset.productId = productId;
      if (subscriberId) this.dataset.subscriberId = subscriberId;

      if (next) {
        this.innerHTML = next.innerHTML;
        this.toggleAttribute('hidden', next.hasAttribute('hidden'));
      } else {
        this.reset();
      }

      this.hydrate();
    }

    reset() {
      this.setAttribute('hidden', '');
      const marker = this.querySelector('[data-vpv-slider-marker]');
      if (marker) marker.style.removeProperty('left');
    }

    hasDefault() {
      const marker = this.querySelector('[data-vpv-slider-marker]');
      return !!(marker && marker.style.left);
    }

    hydrate() {
      const subscriberId = this.dataset.subscriberId ||
        (document.querySelector('meta[name="oke:subscriber_id"]') || {}).content;
      const productId = this.dataset.productId;
      const token = (this._hydrateToken = {});
      if (this.hasDefault()) return;

      if (!subscriberId || !productId || !this.querySelector('[data-vpv-slider]')) return;

      fetchAggregate(subscriberId, productId)
        .then((data) => {
          if (token !== this._hydrateToken || !this.isConnected) return;
          this.apply(data);
        })
        .catch(() => {});
    }

    apply(data) {
      const aggregate = data && data.reviewAggregate;
      const ratings = (aggregate && aggregate.attributeRatings) || [];
      const wanted = (this.dataset.attributeTitle || 'Fit').toLowerCase();
      const attr = ratings.find((rating) => (rating.title || '').toLowerCase() === wanted);

      if (!attr || typeof attr.value !== 'number') return;

      const percent = Math.max(0, Math.min(100,
        ((attr.value - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100));

      const marker = this.querySelector('[data-vpv-slider-marker]');
      if (marker) marker.style.left = percent.toFixed(2) + '%';

      const labels = (this.dataset.labels || '').split(',').map((label) => label.trim());
      const sr = this.querySelector('[data-vpv-slider-sr]');

      if (sr) {
        // Okendo's own a11y convention: the value doubled onto a -2 to 2
        // scale, so screen readers hear the same numbers in both placements.
        const presented = Math.round(attr.value * 2 * 10) / 10;
        const nearest = labels[Math.round((percent / 100) * (labels.length - 1))] || '';

        sr.textContent = nearest + ' — customers rate this ' + presented +
          ' on a scale from ' + (attr.minLabel || labels[0] || '').toLowerCase() + ' (minus 2) to ' +
          (attr.maxLabel || labels[labels.length - 1] || '').toLowerCase() + ' (2)' +
          (aggregate.reviewCount ? ', based on ' + aggregate.reviewCount + ' reviews' : '') + '.';
      }

      this.removeAttribute('hidden');
    }
  }

  if (!customElements.get('vpv-garment-slider-block')) {
    customElements.define('vpv-garment-slider-block', VpvGarmentSliderBlock);
  }
  if (!customElements.get('vpv-okendo-fit-slider')) {
    customElements.define('vpv-okendo-fit-slider', VpvOkendoFitSlider);
  }
})();
